/**
 * Authentication Types for Personal Trainer App
 *
 * This file contains all TypeScript interfaces and types related to
 * authentication, user management, and API authentication flows.
 *
 * Features:
 * - User profile and authentication interfaces
 * - Login/Register request and response types
 * - Token management types
 * - Password reset flow types
 * - Error handling types for authentication
 * - Social sign-in and account security types
 */

import { API_ENDPOINTS } from './api.types';

import type { PlanReview } from './domain/plan.types';
import type { DashboardProfile, Theme } from './index';
import type { Currency } from './user-preferences.types';

// =============================================================================
// BASE USER TYPES
// =============================================================================

export type WorkoutDay =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday';

export type Role = 'client' | 'trainer' | 'admin';

export type PlanName = 'Free Plan' | 'Premium Plan' | 'Pro Plan';

export type MeasurementSystem = 'imperial' | 'metric';

export type Language =
  'en' | 'es' | 'fr' | 'de' | 'it' | 'pt' | 'ru' | 'ja' | 'ko' | 'zh';

/**
 * Session user information
 */
export interface User {
  name: string | null;
  email: string;
  /**
   * Self-reported gender, returned by the API as a display label (e.g.
   * "Non-binary"), not the enum value. Absent until the user sets it.
   */
  gender?: string | null;
  avatar: string;
  role: Role;
  username: string;
  email_verified: boolean;
  /**
   * False for an account made with Google or Apple that has never set a
   * password: it deletes by typing DELETE, and can add a first password.
   */
  has_password: boolean;
  /** The providers that can sign in to the account; empty for password-only. */
  linked_providers: SocialProvider[];
  /**
   * True while `email` is Apple's Hide My Email relay. Show it as the user's
   * address, and never ask them to replace it.
   */
  email_is_private_relay: boolean;
  /** Two-factor is on, so connecting a provider is refused. */
  two_factor_enabled: boolean;
  /** An email change waiting on both addresses to agree, or null. */
  pending_email: string | null;
  preferences: {
    theme_selection: Theme;
    measurement_system: MeasurementSystem;
    language: Language;
    dashboard_profile: DashboardProfile;
    workouts_per_week: number | null;
    preferred_workout_days: WorkoutDay[];
    notification_preferences: Record<string, boolean>;
    privacy_settings: Record<string, string | boolean>;
    /** Always sent (CAD when unset); optional because fixtures predate it. */
    currency?: Currency;
    /** The IANA zone a device last reported, null until one has; optional because fixtures predate it. */
    detected_timezone?: string | null;
  };
  subscription?: {
    plan: PlanName;
    expires_at: string | null;
  };
  features?: {
    public_exercises?: boolean;
    external_exercises?: boolean;
    /**
     * May attach progress photos: two-factor is on, or the account signs in
     * with Google or Apple. False only ever means two-factor is off. The plan
     * is not part of it (read `subscription.plan`). Absent for admins.
     */
    media_uploads?: boolean;
  };
  /**
   * The admin review of the user's current questionnaire answers, when they
   * declare an injury. `null` (or absent, from an API that predates it) when
   * there is none. Drives the dashboard's being-reviewed and declined cards.
   */
  plan_review?: PlanReview | null;
  /**
   * App invitations this account has sent (`POST /api/invite`), against a
   * ceiling of `APP_INVITATION_LIMIT`. Spent ones never come back. Optional
   * because fixtures predate it; read it through `invitationsLeft`.
   */
  app_invitations_sent?: number;
  /**
   * The admin's username while an admin is signed in as this member from the
   * admin panel, else `null`. The session is read-only while it is set, and
   * signing out means leaving (`authService.leaveImpersonation`). Optional
   * because fixtures, and the mobile app's bearer sessions, predate it.
   */
  impersonated_by?: string | null;
}

// =============================================================================
// AUTHENTICATION TOKEN TYPES
// =============================================================================

/**
 * Authentication token information from API
 */
export interface AuthToken {
  access_token: string;
  refresh_token?: string;
  token_type: 'Bearer';
  expires_in: number; // seconds until expiration, as the API sends it
  expires_at?: string; // ISO 8601 datetime string — prefer it over expires_in
  scope?: string[];
}

/**
 * CSRF token information for stateful authentication
 */
export interface CSRFToken {
  token: string;
  expires_at?: string;
}

/**
 * Token refresh response
 */
export interface TokenRefreshResponse {
  access_token: string;
  token_type: 'Bearer';
  expires_in: number;
  expires_at?: string;
}

// =============================================================================
// LOGIN TYPES
// =============================================================================

/**
 * Login request payload
 */
export interface LoginRequest {
  email: string;
  password: string;
  remember_me?: boolean;
  device_name?: string; // For device tracking
}

/**
 * Login response from API (token-based)
 */
export interface LoginResponse {
  user: User;
  token: AuthToken;
  message?: string;
}

/**
 * Login response for stateful authentication (session-based)
 */
export interface SessionLoginResponse {
  user: User;
  message?: string;
  two_factor?: false;
}

/**
 * A sign-in whose password was right, waiting for a two-factor code: the
 * website's `POST /login` answers it as is, and an app's `POST /sanctum/token`
 * with a challenge the auth service keeps.
 */
export interface TwoFactorRequired {
  two_factor: true;
}

/**
 * What signing in with a password came to: signed in, or waiting for a
 * two-factor code before it is.
 */
export type LoginOutcome = 'signed-in' | 'two-factor';

// =============================================================================
// REGISTRATION TYPES
// =============================================================================

/**
 * Result of invitation token validation
 */
export interface TokenValidationResult {
  isValid: boolean;
  email?: string;
  error?: string;
}

/**
 * User registration request payload
 */
export interface RegisterRequest {
  name: string;
  email: string;
  role: 'client' | 'trainer';
  gender?:
    | 'male'
    | 'female'
    | 'non_binary'
    | 'transgender'
    | 'genderqueer'
    | 'agender'
    | 'bigender'
    | 'gender_fluid'
    | 'prefer_not_to_say';
  token: string;
  password: string;
  password_confirmation: string;
}

/**
 * An app's registration answer: the `{ user, token }` a password sign-in
 * gets. The bearer is there whenever `device_name` was sent.
 */
export interface RegisterResponse {
  user: User;
  token?: AuthToken;
  message?: string;
}

/**
 * Registration response for stateful authentication (session-based)
 */
export interface SessionRegisterResponse {
  user: User;
  message: string;
}

/**
 * The four parts of an emailed verification link, as the website's
 * `/verify-email` page reads them from its query. Together they're a signed
 * API call, so they go back exactly as they came.
 */
export interface EmailVerificationLink {
  id: string;
  hash: string;
  expires: string;
  signature: string;
}

/**
 * What asking for a new verification link came to: one was sent, or the
 * account turned out to be verified already.
 */
export type VerificationLinkResult = 'sent' | 'already-verified';

// =============================================================================
// PASSWORD RESET TYPES
// =============================================================================

/**
 * Password reset request payload
 */
export interface PasswordResetRequest {
  email: string;
}

/**
 * Password reset response
 */
export interface PasswordResetResponse {
  message: string;
  status: 'success' | 'error';
}

/**
 * Password reset confirmation payload
 */
export interface PasswordResetConfirmRequest {
  email: string;
  token: string;
  password: string;
  password_confirmation: string;
}

/**
 * Password reset confirmation response (token-based)
 */
export interface PasswordResetConfirmResponse {
  message: string;
  status: 'success' | 'error';
  token?: AuthToken; // Auto-login after password reset
}

/**
 * Password reset confirmation response for stateful authentication
 */
export interface SessionPasswordResetConfirmResponse {
  message: string;
  status: 'success' | 'error';
  user?: User; // User data if auto-login after password reset
}

/**
 * Change password request (for authenticated users)
 */
export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
  new_password_confirmation: string;
}

/**
 * Delete account request (for authenticated users)
 *
 * Account deletion is permanent and immediate, so the caller proves intent. A
 * password account re-supplies its password. An account made with Google or
 * Apple has none (`has_password` is false), so it sends `confirmation:
 * 'DELETE'` instead, exactly and in capitals. Sent as the body of `DELETE
 * /api/account/delete`.
 */
export type DeleteAccountRequest =
  { password: string } | { confirmation: 'DELETE' };

// =============================================================================
// LOGOUT TYPES
// =============================================================================

/**
 * Logout request (optional payload for device-specific logout)
 */
export interface LogoutRequest {
  all_devices?: boolean;
  device_name?: string;
}

/**
 * Logout response
 */
export interface LogoutResponse {
  message: string;
  status: 'success';
}

// =============================================================================
// AUTHENTICATION STATE TYPES
// =============================================================================

/**
 * Current authentication state
 */
export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  token: AuthToken | null;
  mode: AuthMode;
  lastLoginAt?: string;
  sessionExpiry?: string;
  csrfToken?: string;
}

/**
 * Authentication action types for state management
 */
export type AuthAction =
  | { type: 'AUTH_LOGIN_START' }
  | {
      type: 'AUTH_LOGIN_SUCCESS';
      payload: { user: User; token?: AuthToken; mode: AuthMode };
    }
  | { type: 'AUTH_LOGIN_FAILURE'; payload: { error: AuthError } }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'AUTH_REFRESH_TOKEN_SUCCESS'; payload: { token: AuthToken } }
  | { type: 'AUTH_UPDATE_USER'; payload: { user: User } }
  | { type: 'AUTH_SET_LOADING'; payload: { isLoading: boolean } }
  | { type: 'AUTH_SET_CSRF_TOKEN'; payload: { csrfToken: string } }
  | { type: 'AUTH_CLEAR_ERROR' };

// =============================================================================
// ERROR TYPES
// =============================================================================

/**
 * Authentication-specific error types
 */
export interface AuthError {
  code: string;
  message: string;
  field?: string; // Field that caused the error (for validation errors)
  details?: Record<string, string[]>; // Laravel validation error format
  /** The API's `meta`, such as `signs_in_with` on a social `account_exists`. */
  meta?: Record<string, unknown>;
}

/**
 * Common authentication error codes
 */
export const AUTH_ERROR_CODES = {
  // Login errors
  INVALID_CREDENTIALS: 'invalid_credentials',
  ACCOUNT_LOCKED: 'account_locked',
  EMAIL_NOT_VERIFIED: 'email_not_verified',
  TOO_MANY_ATTEMPTS: 'too_many_attempts',

  // Registration errors
  EMAIL_ALREADY_EXISTS: 'email_already_exists',
  WEAK_PASSWORD: 'weak_password',
  TERMS_NOT_ACCEPTED: 'terms_not_accepted',

  // Token errors
  TOKEN_EXPIRED: 'token_expired',
  TOKEN_INVALID: 'token_invalid',
  REFRESH_TOKEN_EXPIRED: 'refresh_token_expired',

  // CSRF errors
  CSRF_TOKEN_MISMATCH: 'csrf_token_mismatch',
  CSRF_TOKEN_EXPIRED: 'csrf_token_expired',
  SESSION_EXPIRED: 'session_expired',

  // Password reset errors
  RESET_TOKEN_EXPIRED: 'reset_token_expired',
  RESET_TOKEN_INVALID: 'reset_token_invalid',
  PASSWORD_RESET_FAILED: 'password_reset_failed',

  // General errors
  UNAUTHORIZED: 'unauthorized',
  FORBIDDEN: 'forbidden',
  NETWORK_ERROR: 'network_error',
  SERVER_ERROR: 'server_error',
  UNKNOWN_ERROR: 'unknown_error',
} as const;

export type AuthErrorCode =
  (typeof AUTH_ERROR_CODES)[keyof typeof AUTH_ERROR_CODES];

/**
 * Validation error response from Laravel
 */
export interface ValidationErrorResponse {
  message: string;
  errors: Record<string, string[]>;
}

// =============================================================================
// API RESPONSE WRAPPERS
// =============================================================================

// API response types are defined in api.types.ts

// =============================================================================
// AUTHENTICATION FLOW TYPES
// =============================================================================

/**
 * A two-factor proof: a six-digit code from the authenticator app, or one of
 * the account's recovery codes. Each works once.
 */
export type TwoFactorProof = { code: string } | { recovery_code: string };

/**
 * The QR code to scan while setting up two-factor: an SVG, and the
 * `otpauth://` URI it encodes.
 */
export interface TwoFactorQrCode {
  svg: string;
  url: string;
}

/**
 * CSRF initialization request/response
 */
export type CSRFInitRequest = Record<string, never>;

export type CSRFInitResponse = Record<string, never>;

/**
 * Device management types
 */
export interface AuthDevice {
  id: string;
  name: string;
  type: 'web' | 'mobile' | 'desktop';
  last_used_at: string;
  ip_address: string;
  user_agent: string;
  is_current: boolean;
}

export interface DeviceListResponse {
  devices: AuthDevice[];
}

export interface RevokeDeviceRequest {
  device_id: string;
}

// =============================================================================
// SOCIAL SIGN-IN TYPES
// =============================================================================

/**
 * A provider an account can sign in with besides its password.
 */
export type SocialProvider = 'google' | 'apple';

/**
 * `POST /api/auth/social/google` — an ID token from the platform's Google
 * Sign-In, requested for the WEB client id: that is the audience the API checks.
 */
export interface GoogleSignInRequest {
  id_token: string;
  device_name?: string; // For device tracking
}

/**
 * `POST /api/auth/social/apple` — what Sign in with Apple returned, with the
 * raw nonce whose SHA-256 was passed to Apple. A nonce works for one request.
 */
export interface AppleSignInRequest {
  identity_token: string;
  authorization_code: string;
  nonce: string;
  device_name?: string; // For device tracking
  /** Apple shares the name on the first authorization only. */
  given_name?: string;
  family_name?: string;
}

/**
 * A social sign-in that found no account. Nothing is made until the user says
 * to, before `expires_at`.
 */
export interface PendingSignUp {
  /** Apps only: sent back to answer it. The website's stays in its session. */
  token?: string;
  provider: SocialProvider;
  email: string;
  /** The new account would use Apple's Hide My Email relay address. */
  email_is_private_relay: boolean;
  name: string | null;
  expires_at: string;
}

/**
 * An app's social sign-in that found the account (`200`): its tokens are
 * stored.
 */
export interface SocialSignedInResult {
  type: 'signed-in';
  response: LoginResponse;
}

/**
 * An app's social sign-in that found no account (`202`): the user decides
 * whether to make one.
 */
export interface SocialSignUpPendingResult {
  type: 'pending';
  pendingSignUp: PendingSignUp;
}

/** What an app's social sign-in came to. */
export type SocialSignInResult =
  SocialSignedInResult | SocialSignUpPendingResult;

/**
 * Why a website sign-in was refused: `/login?sso_error=<code>`. With
 * `account_exists`, `sso_method` says how that account signs in, when it's
 * with a provider (the apps get it as `meta.signs_in_with`).
 */
export type SsoErrorCode =
  | 'cancelled'
  | 'expired'
  | 'unavailable'
  | 'account_exists'
  | 'email_unverified'
  | 'email_missing'
  | 'sign_up_closed';

// =============================================================================
// ACCOUNT SECURITY TYPES
// =============================================================================

/**
 * A website flow that leaves settings for a provider and comes back to
 * `/settings?account_flow=<intent>&result=<result>`.
 */
export type AccountFlowIntent = 'confirm' | 'link';

/** How a website `confirm` or `link` flow ended. */
export type AccountFlowResult =
  | 'confirmed'
  | 'linked'
  | 'not_linked'
  | 'confirmation_required'
  | 'two_factor_on'
  | 'already_linked'
  | 'other_provider_linked'
  | 'mismatch'
  | 'identity_in_use'
  | 'cancelled'
  | 'expired'
  | 'unavailable'
  | 'email_unverified';

/** A way into an account: its password, or a provider it signs in with. */
export type SignInMethod = 'password' | SocialProvider;

/** A way to confirm it's you: the password, or a connected provider. */
export type ConfirmationMethod = SignInMethod;

/**
 * `GET /api/account/confirmation` — what confirming takes, and until when the
 * session or bearer is confirmed.
 */
export interface IdentityConfirmation {
  /** Null when the session or bearer isn't confirmed now. */
  confirmed_until: string | null;
  methods: ConfirmationMethod[];
  two_factor: boolean;
}

/** Confirming with the password, and, with two-factor on, its code. */
export interface PasswordConfirmationProof {
  password: string;
  /** With two-factor on: a code from the authenticator app, */
  code?: string;
  /** or one of the recovery codes. */
  recovery_code?: string;
}

/**
 * `POST /api/account/confirmation` — one proof from `methods`. A provider proof
 * is a fresh sign-in with the platform's SDK, sent with its raw nonce.
 */
export type ConfirmationProof =
  | PasswordConfirmationProof
  | { provider: 'google'; id_token: string; nonce: string }
  | { provider: 'apple'; identity_token: string; nonce: string };

/**
 * `POST /api/account/sign-in-methods/google` — a fresh ID token for the WEB
 * client id, and the raw nonce whose SHA-256 went to Google.
 */
export interface LinkGoogleRequest {
  id_token: string;
  nonce: string;
}

/**
 * `POST /api/account/sign-in-methods/apple` — what Sign in with Apple returned
 * when asked for no scopes, and the raw nonce.
 */
export interface LinkAppleRequest {
  identity_token: string;
  authorization_code: string;
  nonce: string;
}

/**
 * `POST /api/account/password` — a passwordless account's first password, under
 * registration's rules.
 */
export interface AddPasswordRequest {
  password: string;
  password_confirmation: string;
}

// =============================================================================
// UTILITY TYPES
// =============================================================================

/**
 * Authentication method types
 */
export type AuthMethod = 'email' | 'social' | '2fa';

/**
 * Authentication mode types
 */
export type AuthMode = 'token' | 'session';

/**
 * Per-platform authentication configuration, injected once at the app's
 * composition root (see `registerAuthConfig`). Auth mode + transport is a
 * compile-time platform CONSTANT — web is always `session` (stateful cookie +
 * CSRF), mobile is always `token` (stateless bearer) — NOT mutable/persisted
 * runtime state. Each platform registers its config at bootstrap; shared reads
 * it and never mutates or persists it.
 */
export interface AuthConfig {
  /** `token` (mobile, stateless bearer) or `session` (web, stateful cookie/CSRF). */
  mode: AuthMode;
  /** Auth/CSRF base URL (Laravel root, no `/api`). Defaults to the env auth base. */
  baseURL?: string;
  /**
   * Send cookies with auth requests. MUST be `false` for mobile/token — React
   * Native replays Laravel's session + XSRF cookies but can't mirror XSRF-TOKEN
   * into the `X-XSRF-TOKEN` header (no `document.cookie`), so CSRF-checked
   * routes 419. `true` for web/session.
   */
  withCredentials?: boolean;
  /** Mirror the XSRF-TOKEN cookie into the `X-XSRF-TOKEN` header (web/session only). */
  withXSRFToken?: boolean;
}

/**
 * User role types (for future RBAC implementation)
 */
export type UserRole = 'user' | 'trainer' | 'admin';

/**
 * Subscription tier types
 */
export type SubscriptionTier = 'free' | 'premium' | 'pro';

/**
 * Authentication event types for analytics
 */
export type AuthEventType =
  | 'login'
  | 'logout'
  | 'register'
  | 'password_reset'
  | 'email_verification'
  | 'token_refresh'
  | 'profile_update';

export interface AuthEvent {
  type: AuthEventType;
  user_id?: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

// =============================================================================
// TYPE GUARDS AND UTILITY FUNCTIONS
// =============================================================================

/**
 * Type guard to check if an error is an AuthError
 */
export const isAuthError = (error: unknown): error is AuthError => {
  return (
    error !== null &&
    typeof error === 'object' &&
    'code' in error &&
    'message' in error &&
    typeof (error as Record<string, unknown>).code === 'string' &&
    typeof (error as Record<string, unknown>).message === 'string'
  );
};

/**
 * Type guard to check if response is a validation error
 */
export const isValidationError = (
  error: unknown
): error is ValidationErrorResponse => {
  return (
    error !== null &&
    typeof error === 'object' &&
    'errors' in error &&
    typeof (error as Record<string, unknown>).errors === 'object' &&
    (error as Record<string, unknown>).errors !== null
  );
};

/**
 * Type guard to check if user is authenticated
 */
export const isAuthenticatedUser = (user: unknown): user is User => {
  return (
    user !== null &&
    typeof user === 'object' &&
    'id' in user &&
    'email' in user &&
    typeof (user as Record<string, unknown>).id === 'string' &&
    typeof (user as Record<string, unknown>).email === 'string'
  );
};

// =============================================================================
// DEFAULT VALUES AND CONSTANTS
// =============================================================================

/**
 * Default user profile values
 */
// TODO: update when user preferences are defined
// export const DEFAULT_USER_PREFERENCES: Partial<UserProfile> = {
//   subscription_status: 'free',
// };

/**
 * Password strength requirements
 */
export const PASSWORD_REQUIREMENTS = {
  minLength: 8,
  maxLength: 128,
  requireUppercase: true,
  requireLowercase: true,
  requireNumbers: true,
  requireSpecialChars: true,
  forbiddenPatterns: ['password', '123456', 'qwerty'],
} as const;

/**
 * Session configuration
 */
export const SESSION_CONFIG = {
  defaultExpirationMinutes: 60 * 24, // 24 hours
  maxRefreshAttempts: 3,
  rememberMeDays: 30,
} as const;

/**
 * Sanctum configuration
 */
export const SANCTUM_CONFIG = {
  csrfCookiePath: API_ENDPOINTS.CSRF_INIT.path,
  sessionDriver: 'cookie',
  sameSiteCookies: 'lax',
  sessionLifetime: 120, // minutes
} as const;
